import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Button, Spinner, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Alert, Table,} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";

const Addendums = () => {
  const [modal, setModal] = useState(false);
  const [addendums, setAddendums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [addendumsPerPage] = useState(10);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [addendumToDelete, setContractToDelete] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [addendumToEdit, setAddendumToEdit] = useState(null);

  const navigate = useNavigate();

  const toggleModal = () => setModal(!modal);
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const toggleEditModal = () => setEditModal(!editModal);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date) ? dateString : format(date, "PPP"); // Example: "Mar 5, 2025"
  };

  // Fetch addendums from the API
  useEffect(() => {
    const fetchAddendums = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/addendums`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Network response was not ok");
  
        const data = await response.json();
        sessionStorage.setItem("addendum_len", data.length);
  
        // Fetch contract titles for all addendums
        const addendumsWithContracts = await Promise.all(data.map(async (addendum) => {
          if (!addendum.contractId) return { ...addendum, contractTitle: "N/A" };
          
          const contractRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/contract/${addendum.contractId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            },
          });
  
          if (!contractRes.ok) return { ...addendum, contractTitle: "Unknown" };
  
          const contractData = await contractRes.json();
          return { ...addendum, contractTitle: contractData.title };
        }));
  
        // Sort addendums by creation date in descending order
        addendumsWithContracts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  
        setAddendums(addendumsWithContracts);
      } catch (error) {
        console.error("Error fetching addendums:", error);
        setError("Error fetching addendums: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAddendums();
  }, []);

  // useEffect(()=>{
  //   const fetchAddendumData = async ()=>{
  //     const addResponse = await fetch(`${REACT_APP_API_BASE_URL}/api/addendum/${addendumToEdit._id}`)
  //   }
  // }, [addendumToEdit])
  
  // Handle addendum deletion
  const handleDelete = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/addendum/${addendumToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Network response was not ok");
      setSuccess("Contract deleted successfully!");
      setAddendums((prevAddendums) =>
        prevAddendums.filter((addendum) => addendum._id !== addendumToDelete._id)
      );
      toggleDeleteModal();
    } catch (error) {
      console.error("Error deleting addendum:", error);
      setError("Error: " + error.message);
    }
  };

  // Confirm deletion
  const confirmDelete = (addendumId) => {
    setContractToDelete({ _id: addendumId });
    toggleDeleteModal();
  };

  // Handle addendum update
  const handleEdit = async () => {
    try {
      const token = sessionStorage.getItem("authToken");
  
      // Create FormData object
      const formData = new FormData();
      Object.keys(addendumToEdit).forEach((key) => {
        if (addendumToEdit[key] !== null && addendumToEdit[key] !== undefined) {
          if (key === "file" && addendumToEdit[key]) {
            formData.append(key, addendumToEdit[key]); // Append file field
          } else {
            formData.append(key, addendumToEdit[key]); // Append other fields
          }
        }
      });
  
      // Send PATCH request with FormData
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/addendum/${addendumToEdit._id}`,
        {
          method: "PATCH",
          headers: {
            "Authorization": `Bearer ${token}`,
          },
          body: formData,
        }
      );
  
      if (!response.ok) throw new Error("Network response was not ok");
  
      const updatedContract = await response.json();
      setAddendums((prevAddendums) =>
        prevAddendums.map((addendum) =>
          addendum._id === updatedContract._id ? updatedContract : addendum
        )
      );
  
      setSuccess("Contract updated successfully!");
      toggleEditModal();
    } catch (error) {
      console.error("Error updating addendum:", error);
      setError("Error: " + error.message)
    }
  };

  // Confirm edit
  const confirmEdit = (addendum) => {
      const { _id, title, reference, contractId } = addendum;
      
    setAddendumToEdit({ title, _id, reference, contractId });
    toggleEditModal();
  };

  const confirmView = (addendumId) => {
    navigate(`/view-addendum/${addendumId}`); // Correct usage of navigate
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle edit form input change
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setAddendumToEdit({ ...addendumToEdit, [name]: value });
  };

  // Filter addendums based on search term and maintain the sorted order
  const filteredAddendums = addendums
  .filter((addendum) =>
    addendum.title.toLowerCase().includes(searchTerm.toLowerCase())
  )
  .sort((a, b) => new Date(b.created_on) - new Date(a.created_on)); // Re-sort after filtering

  // Get the loggedInUser's role
  const userRole = sessionStorage.getItem("authUser").role;
  // console.log(`User Role ${userRole}`)

  // Paginate addendums
  const indexOfLastAddendum = currentPage * addendumsPerPage;
  const indexOfFirstAddendum = indexOfLastAddendum - addendumsPerPage;
  const currentAddendums = filteredAddendums.slice(
  indexOfFirstAddendum,
  indexOfLastAddendum
  );

  // Paginate function
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div>
      {/* Manage Addendums Header */}
      <Card>
        <CardHeader>Manage Addendums</CardHeader>
        <CardBody>
          {/* Search Bar */}
          <Row>
            <Col md={6}>
              <Input
                type="text"
                placeholder="Search by title..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </Col>
            <Col md={4}></Col>
            <Col md={2} className="float-right">
            { userRole === 'IT' || userRole === 'legal'}
              <Button color="primary" href="add-addendum"> 
                Add Addendum
              </Button>
            </Col>
          </Row>

          {/* Addendums Table */}
          {loading ? (
            <Spinner color="primary" />
          ) : (
            <Table striped>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Reference</th>
                  <th>Contract</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {currentAddendums.length > 0 ? (
                  currentAddendums.map((addendum, index) => (
                    <tr key={addendum._id}>
                      <td>{indexOfFirstAddendum + index + 1}</td>
                      <td>{addendum.title}</td>
                      <td>{addendum.reference}</td>
                      <td>{addendum.contractTitle || addendum.contractId}</td>
                      <td>{formatDate(addendum.createdAt)}</td>
                      <td>
                        <Button color="warning" size="sm" onClick={() => confirmView(addendum._id)}>View</Button>{" "}
                        <Button color="success" size="sm" onClick={() => confirmEdit(addendum)}>Edit</Button>{" "}
                        <Button color="danger" size="sm" onClick={() => confirmDelete(addendum._id)}>Delete</Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">Sorry! No Results Found</td>
                  </tr>
                )}
              </tbody>

            </Table>
          )}

          {/* Pagination */}
          <Row>
            <Col className="d-flex justify-content-between align-items-center">
              <Button
                color="secondary"
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {Array.from(
                { length: Math.ceil(filteredAddendums.length / addendumsPerPage) },
                (_, i) => (
                  <Button
                    key={i + 1}
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => paginate(i + 1)}
                  >
                    {i + 1}
                  </Button>
                )
              )}
              <Button
                color="secondary"
                onClick={() =>
                  paginate(
                    Math.ceil(filteredAddendums.length / addendumsPerPage)
                  )
                }
                disabled={
                  currentPage ===
                  Math.ceil(filteredAddendums.length / addendumsPerPage)
                }
              >
                Next
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Add Contract Modal */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Add Contract</ModalHeader>
        <ModalBody>
          {/* Add Contract Form */}
          <Form>
            {/* Add fields for adding a new addendum here */}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary">Save</Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this addendum?</ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleDelete}>
            Yeah
          </Button>{" "}
          <Button color="secondary" onClick={toggleDeleteModal}>
            Nope
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Contract Modal */}
      <Modal isOpen={editModal} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Addendum</ModalHeader>
        <ModalBody>
          {addendumToEdit && (
            <Form>
              {Object.keys(addendumToEdit).map((key) => {
                if(key === "_id") return null
                return (
                  <FormGroup key={key}>
                    <Label>{key.replace("_", " ")}</Label>
                    <Input
                      type="text"
                      name={key}
                      value={addendumToEdit[key]}
                      onChange={handleEditChange}    
                      readOnly={key === "createdAt" || key === "created_by"}                 
                    />
                  </FormGroup>
                )}
              )}
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleEdit}>
            Save Changes
          </Button>{" "}
          <Button color="secondary" onClick={toggleEditModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Alerts */}
      {error && <Alert color="danger">{error}</Alert>}
      {success && <Alert color="success">{success}</Alert>}
    </div>
  );
};

export default Addendums;